'use client';

import React from 'react';
import { Loader2, Upload, RotateCcw, AlertTriangle, Package, Archive } from 'lucide-react';

interface UploadStickyBarProps {
  isSubmitting: boolean;
  isDirty: boolean;
  hasVideo: boolean;
  onReset: () => void;
  uploadMode?: 'single' | 'bundle' | 'scenepack';
  bundleCount?: number;
}

export default function UploadStickyBar({ isSubmitting, isDirty, hasVideo, onReset, uploadMode = 'single', bundleCount = 0 }: UploadStickyBarProps) {
  const isBundle = uploadMode === 'bundle';
  const isScenepack = uploadMode === 'scenepack';
  const isReady = hasVideo;

  const notReadyLabel = isBundle
    ? 'Add at least one clip to publish'
    : isScenepack
    ? 'Add a scenepack archive to publish' :'Add a video file to publish';

  const readyLabel = isBundle
    ? `${bundleCount} clip${bundleCount !== 1 ? 's' : ''} ready to publish`
    : isScenepack
    ? 'Scenepack ready to publish' :'Ready to publish';

  const emptyLabel = isBundle
    ? 'Add clips above to publish your bundle'
    : isScenepack
    ? 'Upload a scenepack archive above to publish' :'Fill in the form above to upload your clip';

  const publishLabel = isBundle
    ? bundleCount > 0
      ? `Publish ${bundleCount} Clip${bundleCount !== 1 ? 's' : ''}`
      : 'Publish Clips'
    : isScenepack
    ? 'Publish Scenepack' :'Publish Clip';

  const PublishIcon = isBundle ? Package : isScenepack ? Archive : Upload;

  return (
    <div
      className="fixed bottom-16 left-0 right-0 z-40 bottom-nav-blur"
      style={{ background: 'rgba(10, 10, 15, 0.95)', borderTop: '1px solid var(--border)' }}
    >
      <div className="max-w-screen-2xl mx-auto px-4 xl:px-8 2xl:px-12 py-3 flex items-center justify-between gap-4">
        {/* Left: status */}
        <div className="flex items-center gap-2">
          {!isReady && isDirty && (
            <span
              className="flex items-center gap-1.5 text-xs font-medium"
              style={{ color: 'rgba(245, 158, 11, 0.9)' }}
            >
              <AlertTriangle size={13} />
              {notReadyLabel}
            </span>
          )}
          {isReady && (
            <span
              className="flex items-center gap-1.5 text-xs font-medium"
              style={{ color: 'var(--success)' }}
            >
              <PublishIcon size={13} />
              {readyLabel}
            </span>
          )}
          {!isDirty && !isReady && (
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              {emptyLabel}
            </span>
          )}
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onReset}
            disabled={isSubmitting || !isDirty}
            className="btn-ghost flex items-center gap-1.5 px-4 py-2.5 text-sm disabled:opacity-40"
          >
            <RotateCcw size={13} />
            Reset
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !isReady}
            className="btn-primary flex items-center gap-2 px-6 py-2.5 text-sm disabled:opacity-50"
            style={{ minWidth: '160px', justifyContent: 'center' }}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <PublishIcon size={14} />
                {publishLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}